import { useRef, useState, useEffect, useCallback } from "react";
import { FeatureGroup, useMap } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L, { LatLngLiteral } from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw/dist/leaflet.draw-src.css";
import { Box, Flex } from "@chakra-ui/react";
import { useStores } from "stores/setup/use-store";
import { observer } from "mobx-react-lite";
import { CustomVertexEditIcon } from "./util/CustomVertexEditIcon";
import { createEditablePolygon } from "./util/createEditablePolygon";
import { extractCoordinatesFromGeoJSON } from "./util/extractCoordinatesFromGeoJSON";
import { useTranslation } from "react-i18next";
import { getMacroRegionOriginFromPolygon } from "util/macroRegion/getMacroRegionOriginFromPolygon";
import { MacroRegion } from "enum/MacroRegion";
import { DrawMapButtons } from "./DrawMapButtons";
import { InvalidGeofenceAlert } from "./InvalidGeofenceAlert";
import { useHistory, useLocation } from "react-router-dom";

interface PolygonData {
  coordinates: LatLngLiteral[][];
}

function DrawMapControls() {
  const {
    dataStores: {
      forecastChartPageStore,
      insightAlertStore,
      regionStore,
      authStore,
      locationStore,
      oceanWeatherIntelligenceStore,
    },
  } = useStores();

  const drawnItemsRef = useRef<L.FeatureGroup>(null);
  const editToolbarRef = useRef<any>(null);
  const [hasCompletePolygon, setHasCompletePolygon] = useState(false);
  const [polygonData, setPolygonData] = useState<PolygonData | null>(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const map = useMap();

  const history = useHistory();

  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const alertId = queryParams.get("alert_id");
  const locationId = queryParams.get("location_id");
  const operationRuleLocationId = queryParams.get("operation_rule_location_id");
  const eventLocationId = queryParams.get("event_location_id");
  const createAlert = queryParams.get("create_alert");
  const origin = queryParams.get("origin");

  useEffect(() => {
    const hasAlertGeofence = insightAlertStore.selectedGeofence?.length > 0;
    const hasOperationRuleLocationGeofence =
      oceanWeatherIntelligenceStore.selectedGeofence?.length > 0;
    const hasLocationCoordinates =
      locationStore.selectedCoordinates?.length > 0;
    const isDrawingMode = forecastChartPageStore.isAreaDrawingMode;

    if (hasAlertGeofence && alertId) {
      return initializeGeofence();
    }

    if (
      hasAlertGeofence &&
      !alertId &&
      !operationRuleLocationId &&
      !eventLocationId &&
      !locationId &&
      isDrawingMode
    ) {
      return initializeGeofence();
    }

    // Handle event location (from Event Manager)
    if (hasOperationRuleLocationGeofence && eventLocationId) {
      return initializeOperationRuleLocationGeofence();
    }

    if (hasOperationRuleLocationGeofence && operationRuleLocationId) {
      return initializeOperationRuleLocationGeofence();
    }

    if (hasLocationCoordinates && locationId) {
      return initializeLocationGeofence();
    }

    if (
      !hasAlertGeofence &&
      !hasOperationRuleLocationGeofence &&
      !hasLocationCoordinates &&
      isDrawingMode &&
      map
    ) {
      return initializeDrawControl(map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    forecastChartPageStore.isAreaDrawingMode,
    insightAlertStore.selectedGeofence,
    oceanWeatherIntelligenceStore.selectedGeofence,
    locationStore.selectedCoordinates,
    map,
    locationId,
    alertId,
    operationRuleLocationId,
    eventLocationId,
  ]);

  useEffect(() => {
    const leafletTopElement = document.querySelector(".leaflet-top");

    if (leafletTopElement) {
      if (forecastChartPageStore.isAreaDrawingMode) {
        leafletTopElement.classList.add("drawing-mode");
      } else {
        leafletTopElement.classList.remove("drawing-mode");
      }
    }
  }, [forecastChartPageStore.isAreaDrawingMode]);

  useEffect(() => {
    map.on(L.Draw.Event.CREATED, onPolygonCreated);
    map.on(L.Draw.Event.EDITVERTEX, onPolygonEdited);

    return () => {
      map.off(L.Draw.Event.CREATED, onPolygonCreated);
      map.off(L.Draw.Event.EDITVERTEX, onPolygonEdited);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  function initializeGeofence() {
    clearDrawnItems();

    const polygon = createEditablePolygon(
      insightAlertStore.selectedGeofence,
      drawnItemsRef.current
    );

    const geoJSON = polygon.toGeoJSON();
    const coordinates = extractCoordinatesFromGeoJSON(geoJSON);

    setPolygonData({ coordinates });
    setHasCompletePolygon(true);

    enableEditToolbar();

    return () => disableEditToolbar();
  }

  function initializeLocationGeofence() {
    clearDrawnItems();

    const polygon = createEditablePolygon(
      locationStore.selectedCoordinates,
      drawnItemsRef.current
    );

    const geoJSON = polygon.toGeoJSON();
    const coordinates = extractCoordinatesFromGeoJSON(geoJSON);

    setPolygonData({ coordinates });
    setHasCompletePolygon(true);

    enableEditToolbar();

    return () => disableEditToolbar();
  }

  function initializeOperationRuleLocationGeofence() {
    clearDrawnItems();

    const polygon = createEditablePolygon(
      oceanWeatherIntelligenceStore.selectedGeofence,
      drawnItemsRef.current
    );

    const geoJSON = polygon.toGeoJSON();
    const coordinates = extractCoordinatesFromGeoJSON(geoJSON);

    setPolygonData({ coordinates });
    setHasCompletePolygon(true);

    enableEditToolbar();

    return () => disableEditToolbar();
  }

  const initializeDrawControl = useCallback((map: L.Map) => {
    setHasCompletePolygon(false);
    setIsEditingMode(false);
    drawnItemsRef.current?.clearLayers();

    if (editToolbarRef.current) {
      try {
        editToolbarRef.current.disable();
      } catch {}
      editToolbarRef.current = null;
    }

    const drawControl = new L.Draw.Polygon(map as any, {
      shapeOptions: {
        color: "#27272A",
        fillColor: "#27272A",
        fillOpacity: 0.2,
      },
      //@ts-ignore
      guideLayers: drawnItemsRef.current || undefined,
    });

    drawControl.enable();

    return () => drawControl.disable();
  }, []);

  const disableEditToolbar = useCallback(() => {
    if (!editToolbarRef.current) return;
    try {
      editToolbarRef.current.disable();
    } catch {}
    editToolbarRef.current = null;
  }, []);

  const clearDrawnItems = useCallback(() => {
    drawnItemsRef.current?.clearLayers();
  }, []);

  const onPolygonCreated = useCallback(
    (e: any) => {
      const { layerType, layer } = e;

      if (layerType === "polygon") {
        drawnItemsRef.current?.addLayer(layer);

        const tempGroup = L.featureGroup([layer]);
        const geoJSON = tempGroup.toGeoJSON();
        const coordinates = extractCoordinatesFromGeoJSON(geoJSON);

        setPolygonData({ coordinates });

        const macroRegion = getMacroRegionOriginFromPolygon(
          coordinates,
          regionStore.macroRegions,
          authStore.userMarcroRegions as MacroRegion[]
        );

        if (macroRegion) {
          insightAlertStore.setMacroRegionOrigin(macroRegion);
          insightAlertStore.setIsGeofenceInvalid(false);
        } else {
          insightAlertStore.setIsGeofenceInvalid(true);
        }

        setHasCompletePolygon(true);
        //@ts-ignore
        if (map._drawControl) {
          //@ts-ignore
          map._drawControl.disable();
        }

        if (drawnItemsRef.current) {
          if (editToolbarRef.current) {
            try {
              editToolbarRef.current.disable();
            } catch {}
            editToolbarRef.current = null;
          }

          editToolbarRef.current = new (L.EditToolbar.Edit as any)(map, {
            featureGroup: drawnItemsRef.current,
            selectedPathOptions: { maintainColor: true },
            poly: { icon: CustomVertexEditIcon() },
          });

          editToolbarRef.current.enable();
          setIsEditingMode(true);

          const bounds = drawnItemsRef?.current?.getBounds();
          if (bounds?.isValid()) {
            map.fitBounds(bounds, { padding: [50, 50] });
          }
        }
      }
    },
    [
      authStore.userMarcroRegions,
      insightAlertStore,
      map,
      regionStore.macroRegions,
    ]
  );

  const onPolygonEdited = useCallback(() => {
    const layers = drawnItemsRef.current?.getLayers();

    if (!layers || layers.length === 0) return;

    const polygonLayer = layers[0];

    const geoJSON = (polygonLayer as L.Polygon).toGeoJSON();
    const coordinates = extractCoordinatesFromGeoJSON(geoJSON);
    setPolygonData({ coordinates });

    const macroRegion = getMacroRegionOriginFromPolygon(
      coordinates,
      regionStore.macroRegions,
      authStore.userMarcroRegions as MacroRegion[]
    );

    if (macroRegion) {
      insightAlertStore.setMacroRegionOrigin(macroRegion);
      insightAlertStore.setIsGeofenceInvalid(false);
    } else {
      insightAlertStore.setIsGeofenceInvalid(true);
    }
  }, [
    authStore.userMarcroRegions,
    insightAlertStore,
    regionStore.macroRegions,
  ]);

  const enableEditToolbar = useCallback(() => {
    if (!drawnItemsRef.current) return;

    disableEditToolbar();

    editToolbarRef.current = new (L.EditToolbar.Edit as any)(map, {
      featureGroup: drawnItemsRef.current,
      selectedPathOptions: { maintainColor: true },
      poly: { icon: CustomVertexEditIcon() },
    });

    editToolbarRef.current.enable();
    setIsEditingMode(true);

    const bounds = drawnItemsRef?.current?.getBounds();
    if (bounds?.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [disableEditToolbar, map]);

  const saveLocationCoordinates = useCallback(
    (coordinates: LatLngLiteral[][], locationId: string) => {
      locationStore.setSelectedCoordinates(coordinates);

      const redirectUrl =
        locationId === "new"
          ? "/locations?create_location=true"
          : `/locations?edit_location_id=${locationId}`;

      history.push(redirectUrl);
    },
    [locationStore, history]
  );

  const saveAlertGeofence = useCallback(
    (
      coordinates: LatLngLiteral[][],
      alertId?: string,
      isFromInsightAlerts?: boolean,
      isFromInsightManager?: boolean
    ) => {
      // Update operationRuleAlert geofence using setter for proper MobX reactivity
      // Clear locationId since user is drawing a custom area (not selecting a saved location)
      if (insightAlertStore.operationRuleAlert) {
        insightAlertStore.setOperationRuleAlert({
          ...insightAlertStore.operationRuleAlert,
          geofence: coordinates,
          locationId: undefined, // Clear locationId when drawing custom geofence
          area: undefined, // Clear area name as well
        });
      }

      // Also store in selectedGeofence for cases where operationRuleAlert might be null
      insightAlertStore.setSelectedGeofence(coordinates);

      // Set alert location type to "area" since user drew a custom area
      insightAlertStore.setAlertLocationType("area");
      // Clear the saved location flag since this is a custom drawn area
      insightAlertStore.setIsGeofenceSavedToLocation(false);
      // Clear any station selection since user chose area instead
      insightAlertStore.setSelectedStationForAlert(null);

      // If creating event from insight-manager, redirect back with drawer open param
      if (isFromInsightManager) {
        history.push("/insight-manager?tab=1&open_create_drawer=true");
      }
      // If creating alert from insight-alerts, redirect back with drawer open param
      else if (isFromInsightAlerts) {
        history.push("/insight-alerts?open_create_drawer=true");
      } else if (alertId) {
        // Add returning_from_selection flag to prevent data reload
        history.push(
          `/insight-alerts?alert_id=${alertId}&returning_from_selection=true`
        );
      }
    },
    [insightAlertStore, history]
  );

  const saveOperationRuleLocationGeofence = useCallback(
    (coordinates: LatLngLiteral[][], operationRuleLocationId: string) => {
      oceanWeatherIntelligenceStore.setSelectedGeofence(coordinates);

      const redirectUrl =
        operationRuleLocationId === "new"
          ? "/insight-manager?tab=1&create_location=true"
          : `/insight-manager?tab=1&edit_location_id=${operationRuleLocationId}`;

      history.push(redirectUrl);
    },
    [oceanWeatherIntelligenceStore, history]
  );

  const saveEventLocationGeofence = useCallback(
    (coordinates: LatLngLiteral[][], eventLocationId: string) => {
      oceanWeatherIntelligenceStore.setSelectedGeofence(coordinates);

      const redirectUrl = `/insight-manager?tab=1&edit_event_location_id=${eventLocationId}`;

      history.push(redirectUrl);
    },
    [oceanWeatherIntelligenceStore, history]
  );

  const finalizeSaveOperation = useCallback(() => {
    forecastChartPageStore.setIsAreaDrawingMode(false);
    setIsEditingMode(false);
    insightAlertStore.setIsGeofenceSavedToLocation(false);
    disableEditToolbar();
  }, [forecastChartPageStore, disableEditToolbar, insightAlertStore]);

  const onSave = useCallback(() => {
    if (!polygonData?.coordinates) {
      return;
    }

    const { coordinates } = polygonData;

    const isFromInsightAlerts =
      createAlert === "true" && origin === "insight-alerts";
    const isFromInsightManager =
      createAlert === "true" && origin === "insight-manager";

    if (locationId) {
      saveLocationCoordinates(coordinates, locationId);
    } else if (eventLocationId) {
      saveEventLocationGeofence(coordinates, eventLocationId);
    } else if (operationRuleLocationId) {
      saveOperationRuleLocationGeofence(coordinates, operationRuleLocationId);
    } else {
      saveAlertGeofence(
        coordinates,
        alertId || undefined,
        isFromInsightAlerts,
        isFromInsightManager
      );
    }

    finalizeSaveOperation();
  }, [
    polygonData,
    locationId,
    eventLocationId,
    operationRuleLocationId,
    alertId,
    createAlert,
    origin,
    saveLocationCoordinates,
    saveEventLocationGeofence,
    saveOperationRuleLocationGeofence,
    saveAlertGeofence,
    finalizeSaveOperation,
  ]);

  const onCancel = useCallback(() => {
    if (locationId) {
      if (locationId === "new") {
        history.push("/locations?create_location=true");
      } else {
        history.push(`/locations?edit_location_id=${locationId}`);
      }
    } else if (eventLocationId) {
      // If editing event location, redirect back to event manager
      history.push(
        `/insight-manager?tab=1&edit_event_location_id=${eventLocationId}`
      );
    } else if (operationRuleLocationId) {
      if (operationRuleLocationId === "new") {
        history.push("/insight-manager?tab=1&create_location=true");
      } else {
        history.push(
          `/insight-manager?tab=1&edit_location_id=${operationRuleLocationId}`
        );
      }
    } else if (createAlert === "true" && origin === "insight-manager") {
      // If creating event from insight-manager, redirect back with drawer open param
      history.push("/insight-manager?tab=1&open_create_drawer=true");
    } else if (createAlert === "true" && origin === "insight-alerts") {
      // If creating alert from insight-alerts, redirect back with drawer open param
      history.push("/insight-alerts?open_create_drawer=true");
    } else if (alertId) {
      // If no polygon was drawn, clear the selectedGeofence so the drawer shows the draw options
      if (!hasCompletePolygon) {
        insightAlertStore.setSelectedGeofence([]);
      }
      history.push(`/insight-alerts?alert_id=${alertId}`);
    }

    clearDrawnItems();
    disableEditToolbar();
    setHasCompletePolygon(false);
    setIsEditingMode(false);
    forecastChartPageStore.setIsAreaDrawingMode(false);

    if (
      !insightAlertStore.isGeofenceLoadedByGeoJson &&
      !locationStore.isGeofenceLoadedByGeoJson
    ) {
      insightAlertStore.setIsGeofenceInvalid(false);
    }
  }, [
    locationId,
    eventLocationId,
    operationRuleLocationId,
    alertId,
    createAlert,
    origin,
    history,
    clearDrawnItems,
    disableEditToolbar,
    forecastChartPageStore,
    insightAlertStore,
    locationStore.isGeofenceLoadedByGeoJson,
    hasCompletePolygon,
  ]);

  const onClearArea = useCallback(() => {
    clearDrawnItems();
    setPolygonData(null);
    insightAlertStore.setIsGeofenceInvalid(false);
    insightAlertStore.setIsGeofenceSavedToLocation(false);
    setHasCompletePolygon(false);
    initializeDrawControl(map);
    insightAlertStore.setMacroRegionOrigin(null);
  }, [clearDrawnItems, insightAlertStore, initializeDrawControl, map]);

  return (
    <>
      <Box
        position="absolute"
        top="7px"
        left="20px"
        zIndex={1000}
        background="white"
        padding="16px"
        borderRadius="8px"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
        width="300px"
        maxWidth="300px"
      >
        <Flex direction="column" gap={4}>
          <DrawMapButtons
            isEditingMode={isEditingMode}
            polygonDataExists={!!polygonData?.coordinates?.length}
            onCancel={onCancel}
            onSave={onSave}
            onClearArea={onClearArea}
            t={t}
            allowConfirm={
              hasCompletePolygon && !insightAlertStore.isGeofenceInvalid
            }
          />
          {insightAlertStore.isGeofenceInvalid && (
            <InvalidGeofenceAlert t={t} />
          )}
        </Flex>
      </Box>

      <LeafletDrawFeatureGroup drawnItemsRef={drawnItemsRef} />
    </>
  );
}

export default observer(DrawMapControls);

function LeafletDrawFeatureGroup({
  drawnItemsRef,
}: {
  drawnItemsRef: React.RefObject<L.FeatureGroup>;
}) {
  return (
    <FeatureGroup ref={drawnItemsRef}>
      <EditControl
        position="topleft"
        draw={{
          polygon: false,
          rectangle: false,
          circle: false,
          circlemarker: false,
          marker: false,
          polyline: false,
        }}
        edit={{
          edit: false,
          remove: false,
        }}
      />
    </FeatureGroup>
  );
}
